<template>
    <b-container fluid class="flex-grow-1 d-flex flex-column p-0">
        <div class="p-3" v-show="this.$route.name === 'properties'">
            <section class="grid-filters">
                <b-row align-h="between" class="mb-3">
                    <b-col cols="12" class="pl-1 d-none d-md-block mb-3 text-right" v-if="!actionsMenuVisible">
                      <portal-target name="properties-subnav" />
                    </b-col>
                    <b-col
                            cols="12"
                            sm="5"
                            md="4"
                            class="order-0 d-sm-block mb-3 mb-sm-0"
                            :class="{'d-none': !showSearch}"
                    >
                        <search-filter
                                :showSearch="showSearch"
                                v-model="query.search"
                        >
                        </search-filter>
                    </b-col>
                    <b-col col sm="auto" class="text-center">
                        <b-form-group class="mb-0">
                            <b-form-radio-group v-model="toggle.live.value"
                                                buttons
                                                button-variant="outline-teal"
                                                class="w-100 text-nowrap radio-buttons"
                                                size="sm"
                                                :options="toggle.live.options">
                            </b-form-radio-group>
                        </b-form-group>
                    </b-col>
                </b-row>
                <portal to="properties-subnav">
                    <b-dropdown
                            no-caret
                            right
                            variant="outline-primary"
                            toggle-class="py-1 px-2"
                    >
                        <template slot="button-content">
                            Actions
                            <font-awesome-icon
                                    class="ml-1"
                                    :icon="['fal', 'chevron-down']"
                                    size="xs"
                            ></font-awesome-icon>
                        </template>
                        <b-dropdown-item
                                v-if="$can($perm.PROPERTIES_EDIT_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES)"
                                @click="openModal('updateDesignation')">
                            <font-awesome-icon
                                    :icon="['fal', 'pen-alt']"
                            >
                            </font-awesome-icon>
                            Update client property designation
                        </b-dropdown-item>
                        <b-dropdown-item
                                v-if="$can($perm.PROPERTIES_ADD_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES)"
                                @click="openModal('add')">
                            <font-awesome-icon
                                    :icon="['fal', 'plus']"
                            >
                            </font-awesome-icon>
                            Add property
                        </b-dropdown-item>
                        <b-dropdown-item @click="onClickDownloadCsv">
                            <font-awesome-icon
                                    :icon="['fal', 'download']"
                                    size="sm"
                            >
                            </font-awesome-icon>
                            Download CSV
                        </b-dropdown-item>
                    </b-dropdown>
                </portal>
            </section>

            <b-row class="flex-grow-1">
                <b-col class="flex-grow-1 d-flex flex-column">
                    <b-row class="grid-count mb-3 justify-content-between align-items-baseline">
                        <b-col cols="8" class="d-flex align-items-baseline text-dark">
                            <span>
                              {{ count }} Results
                              <span class="font-weight-bold"
                                    v-if="selectedCount > 0"
                              >
                                | {{ selectedCount }} selected
                              </span>
                            </span>
                        </b-col>
                        <b-col cols="4" class="d-flex justify-content-end">
                            <reset-filters
                                    @click="resetGridToDefaultOptions"></reset-filters>
                            <refresh-button
                                    @click="refresh"
                                    :spinning="refreshing"
                            ></refresh-button>
                        </b-col>
                    </b-row>
                    <div class="grid-wrapper flex-grow-1">
                        <ag-grid-vue
                                :style="'width: ' + gridWidth +'%; height: 76vh;'"
                                class="ag-theme-balham"

                                :gridOptions="gridOptions"
                                :defaultColDef="defaultColDef"
                                :columnDefs="columnDefs"
                                :rowData="rowData"

                                :rowSelection="rowSelection"

                                :side-bar="sideBarDef"
                                @gridReady="onGridReady"
                                :floatingFilter="floatingFilter"
                                :quickFilter="query.search"
                                :cacheQuickFilter="cacheQuickFilter"

                                :getContextMenuItems="getContextMenuItems"
                                :suppressExcelExport="suppressExcelExport"

                                @rowSelected="updateRowSelected"

                                @cellClicked="onCellClicked"
                                :headerHeight="rowHeight"
                                :rowHeight="rowHeight"

                                @columnMoved="saveGridOptions"
                                @columnPinned="saveGridOptions"
                                @columnResized="saveGridOptions"
                                @columnVisible="saveGridOptions"
                                @filterChanged="saveGridOptions"
                                @sortChanged="saveGridOptions"
                        ></ag-grid-vue>
                    </div>
                </b-col>
            </b-row>
        </div>
        <add-property-modal
                v-if="modals.add.visible"
                :show.sync="modals.add.visible"
                @refresh-all="refresh"
        >
        </add-property-modal>
        <update-status-modal
                v-if="modals.updateStatus.visible"
                :show.sync="modals.updateStatus.visible"
                :node-ids="selected.nodeIds"
                @refresh-all="refresh"
        >
        </update-status-modal>
        <update-designation-modal
                v-if="modals.updateDesignation.visible"
                :show.sync="modals.updateDesignation.visible"
                :node-ids="selected.nodeIds"
                @refresh-all="refresh"
        >
        </update-designation-modal>
        <router-view name="property" @refresh-all="refresh"></router-view>
    </b-container>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import {
  propertyGridDefaultColDef as gridDefaultColDef,
  propertyGridColumns as gridColumns,
} from '@/services/Property/PropertyListingGrid';
import CaretIcon from '@/components/Form/CaretIcon';
import GridMixin from '@/components/Grid/GridMixin';
import Property from '@/services/Property/Property';
import AddPropertyModal from '@/components/Property/Modal/AddPropertyModal';
import UpdateDesignationModal
  from '@/components/Property/Modal/UpdateDesignationModal';
import UpdateStatusModal from '@/components/Property/Modal/UpdateStatusModal';
import ResetFilters from '@/components/Grid/ResetFilters';
import RefreshButton from '@/components/Grid/RefreshButton';
import SearchFilter from '@/components/Grid/SearchFilter';
import AssessmentType from '@/services/Assessment/AssessmentType';
import { forEach, size } from 'lodash';

export default {
  name: 'Properties',
  components: {
    UpdateDesignationModal,
    UpdateStatusModal,
    AgGridVue,
    CaretIcon,
    AddPropertyModal,
    ResetFilters,
    RefreshButton,
    SearchFilter,
  },
  mixins: [GridMixin],
  data() {
    return {
      modelId: 'propertyId',
      allRows: [],
      defaultColDef: gridDefaultColDef,
      columnDefs: gridColumns,
      toggle: {
        live: {
          value: true,
          options: [
            {
              text: 'Live',
              value: true,
            },
            {
              text: 'Non-live',
              value: false,
            },
            {
              text: 'All',
              value: null,
            },
          ],
        },
      },
      modals: {
        add: {
          visible: false,
        },
        updateStatus: {
          visible: false,
        },
        updateDesignation: {
          visible: false,
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      showSearch: 'propertyShowSearch',
      actionsMenuVisible: 'actionsMenuVisible',
    }),
  },

  watch: {
    'toggle.live.value': {
      handler(newLive) {
        this.updateLiveFilter(newLive);
      },
    },

    '$store.getters.propertyTenure': function (value) {
      this.refresh();
    },
  },

  methods: {
    postGridReady(params) {
      // Keeping this as a commented out method as they will soon want this
      // re-enabled but with different some tweaked logic
      // this.setRowClasses();

      this.refresh();
    },

    /**
     * Async
     * Refreshes the table
     * Sets the grid options afterwards
     */
    async refresh() {
      this.refreshing = true;
      // Wait until the data is set, external dropdown filters are set
      await this.refreshData();
      // Then we set the grid options
      // We set a timeout to disallow any future grid saving that happens
      // As filters are applied immediately after;
      setTimeout(() => {
        this.refreshing = false;
      }, 1);

      // Then we set the grid options
      this.setGridOptions();

      this.postRefresh();
    },

    /**
     * Call after every refresh
     * */
    postRefresh() {
      this.updateRowSelected();
      this.updateLiveFilter(this.toggle.live.value);
    },

    /**
     * Sets rowdata with data from the server
     */
    async refreshData() {
      this.refreshing = true;

      try {
        const [assessmentTypesResponse, propertiesResponse] = await Promise.all([
          AssessmentType.list({ parentTypeId: 0 }),
          Property.all({
            params: {
              limit: 5000,
              'filter[tenure]': this.$store.getters.propertyTenure,
            }
          })
        ]);

        const assessmentTypes = assessmentTypesResponse.data.data;
        const properties = propertiesResponse.data.data;

        properties.forEach(property => {
          const assessmentType = {};
          const filterOptions = [];

          forEach(assessmentTypes, (assessmentTypeValue) => {
            const enabled = `assessmentType_${assessmentTypeValue.assessmentTypeId}_enabled`;

            if (property[enabled]) {
              filterOptions.push(assessmentTypeValue.assessmentType);

              assessmentType[assessmentTypeValue.assessmentTypeId] = {
                display: property[enabled],
                type: assessmentTypeValue,
              };
            }
          });

          property.filterOptions = filterOptions;
          property.assessmentsToDo = assessmentType;
        });

        this.rowData = properties;

        this.allRows = [...this.rowData];
      } catch (e) {
        this.$notify({
          duration: 5000,
          title: 'Error',
          text: 'We couldn\'t obtain the properties right now. Please try again later.',
          type: 'error',
        });

        return [];
      } finally {
        this.refreshing = false;
      }
    },

    onCellClicked(node) {
      if (node.column.colDef.checkboxSelection) {
        return;
      }

      const { propertyId } = node.data;
      this.$router.push({
        name: 'property',
        params: {
          propertyId,
          fromRoute: 'properties',
        },
        query: {
          tab: 'properties',
        },
      });
    },

    /**
     * Set row classes
     */
    setRowClasses() {
      const now = dayjs();
      const threeMonths = dayjs().add(3, 'month');

      this.gridOptions.getRowClass = ({ data }) => {
        if (!data) {
          return '';
        }

        if (data.dueDate) {
          if (now.isAfter(dayjs(data.dueDate))) {
            return 'red-row';
          }
          if (threeMonths.isAfter(dayjs(data.dueDate))) {
            return 'orange-row';
          }
        }

        return '';
      };
    },

    /**
     * Sets the grid up with:
     * Column state
     * Sort
     * Filters
     */
    setGridOptions() {
      const name = 'properties';

      let gridState = this.$store.getters.gridState(name);
      let gridSort = this.$store.getters.gridSort(name);
      let gridFilter = this.$store.getters.gridFilter(name);

      if (!gridState) {
        gridState = this.gridDefaults.state;
      }

      if (!gridSort) {
        gridSort = this.gridDefaults.sort;
      }

      if (!gridFilter) {
        gridFilter = this.gridDefaults.filter;
      }

      this.columnApi.setColumnState(gridState);

      this.gridApi.setSortModel(gridSort);
      this.gridApi.setFilterModel(gridFilter);
      this.gridApi.onFilterChanged();
    },

    /**
     * Only allow to save the grid if it is not refreshing data
     * */
    saveGridOptions() {
      if (this.refreshing) {
        return false;
      }

      return this.saveOptions();
    },

    /**
     * Save the grid options (only allowed once per second)
     */
    saveOptions: debounce(function () {
      const name = 'properties';
      const state = this.columnApi.getColumnState();
      const sort = this.gridApi.getSortModel();
      const filter = this.gridApi.getFilterModel();

      this.$store.dispatch('saveGridOptions', {
        name,
        state,
        sort,
        filter,
      });
    }, 1000),

    /**
     * Opens a modal by name (in data -> modals.[name])
     */
    openModal(name) {
      if (this.refreshing) {
        this.$notify({
          title: 'Please wait..',
          text: 'Properties are currently being loaded, please try again',
          type: 'warn',
        });
        return false;
      }

      if (this.selectedIds.length === 0 && name !== 'add') {
        this.$notify({
          title: 'No properties selected',
          text: 'Please select at least one property.',
          type: 'warn',
        });
        return false;
      }

      this.modals[name].visible = true;
      return true;
    },

    updateLiveFilter(value) {
      if (value === null) { // this is a all data
        this.rowData = this.allRows;
      } else if (value) { // this is 'live' rows only
        this.rowData = this.allRows.filter(node => node.status === 'Live');
      } else { // this is 'non-live' only
        this.rowData = this.allRows.filter(node => node.status !== 'Live');
      }

      this.gridApi.onFilterChanged();
    },
    onClickDownloadCsv() {
      const onlySelected = this.getSelectedNodes().length > 0;
      const params = {
        onlySelected,
      };

      this.gridApi.exportDataAsCsv(params);
    },
  },
};
</script>

<style lang="scss" scoped>
    .radio-buttons /deep/ {
        .btn {
            min-width: 100px;
        }
    }
</style>
